import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabaseClient";

interface CalendarCheckResponse {
  status: string;
  error?: string;
  missingVars?: string[];
  configuredProviders?: string[];
  message?: string;
}

export const useCalendarConfig = () => {
  return useQuery<CalendarCheckResponse>({
    queryKey: ["calendar-config"],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/calendar-check`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      const json: CalendarCheckResponse = await res.json();
      
      if (!res.ok) throw new Error(json.error || "Configuration check failed");

      // Even if successful, check for missing vars warning
      if (json.missingVars && json.missingVars.length > 0) {
        console.warn(`Some providers not configured: ${json.missingVars.join(", ")}`);
      }

      return json;
    },
    retry: 1,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes - config doesn't change often
  });
};

// Helper hook for easier access to config state
export const useCalendarConfigState = () => {
  const { data, isLoading, error } = useCalendarConfig();

  return {
    configuredProviders: data?.configuredProviders || [],
    backendConfigError: error?.message || null,
    isCheckingBackendConfig: isLoading,
    missingVars: data?.missingVars || [],
    configMessage: data?.message || null,
  };
};
